import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TaskLog } from '@ml/tasks/types/classes/task-log.class';
import { Task, TaskLogMeasurement } from '@ml/tasks/types/classes';
import { CrudService } from '@ml/shared/services/crud.service';
import { combineLatest, defer, from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { Params } from '@angular/router';
import { ConnectionService } from '@ml/core/services/connection.service';
import { UserService } from '@ml/core/services';
import { TaskIDBService } from '@ml/tasks/services/task-idb.service';
import { StopLogInterface } from '@ml/tasks/types/interfaces';
import { User } from '@ml/core/types/classes';

@Injectable({
  providedIn: 'root'
})
export class TaskLogsService extends CrudService<TaskLog> {
  private task: Task;

  constructor(
    private http: HttpClient,
    private taskLogsIDB: TaskIDBService,
    private connectionService: ConnectionService,
    private userService: UserService
  ) {
    super(http, false);
  }

  setTask(task: Task) {
    this.task = task;
  }

  get url(): string {
    return `tasks/${this.task.id}/logs`;
  }

  getAll(queryParams: Params): Observable<TaskLog[]> {
    return this.http
      .get<TaskLog[]>(`task-logs`, {
        params: queryParams
      })
      .pipe(catchError(error => of([])));
  }

  list(): Observable<TaskLog[]> {
    return this._http.get<TaskLog[]>(this.url).pipe(
      catchError(error => of([])),
      switchMap((values: TaskLog[]) => this.mergeLogs(values)),
      switchMap(values => {
        this.values$.next(values);
        return this.values$;
      })
    );
  }

  add(value: TaskLog) {
    /**
     * If there is no connection we need to add the log in local storage
     */
    let addObservable: Observable<TaskLog> = null;
    if (!this.connectionService.connection) {
      addObservable = this.createLocalTaskLog(value);
    } else {
      addObservable = this.addRequest(value);
    }

    return addObservable.pipe(tap((_value: TaskLog) => this.addValue(_value)));
  }

  stop(
    taskLog: TaskLog,
    extraInformation?: string,
    taskLogMeasurements?: TaskLogMeasurement[]
  ): Observable<TaskLog> {
    /**
     * If there is no  internet store the request on idb
     */
    const body: StopLogInterface = {};

    if (extraInformation) {
      body.extraInformation = extraInformation;
    }
    if (taskLogMeasurements) {
      body.measurementUnits = taskLogMeasurements;
    }

    let stopObservable: Observable<TaskLog> = null;
    if (!this.connectionService.connection) {
      // if there is no internet store the task log to `idb` database
      stopObservable = this.stopLocalTaskLog(taskLog, body);
    } else {
      stopObservable = this.stopRequest(taskLog.id, body);
    }

    return stopObservable.pipe(
      tap((_value: TaskLog) => this.updateValue(taskLog, _value))
    );
  }

  submitCachedLogs() {
    const stoppedLogs$ = this.getCachedStoppedLogs();
    const cachedLogs$ = this.getCachedLogs();

    return combineLatest([cachedLogs$, stoppedLogs$]).pipe(
      map(([_cachedLogs, _stoppedLogs]) => {
        const cachedLogs = _cachedLogs.map(value => {
          delete value.id;
          value.taskId = value.task.id;
          value.employee = (value.employee as User).id;
          return value;
        });
        return [cachedLogs, _stoppedLogs];
      }),
      filter(
        ([_cachedLogs, _stoppedLogs]) =>
          _cachedLogs.length > 0 || _stoppedLogs.length > 0
      ),
      switchMap(([_cachedLogs, _stoppedLogs]) => {
        const submittedBulkLogs =
          _cachedLogs.length > 0 ? this.submitBulkLogs(_cachedLogs) : of([]);
        const submittedBulkStopLogs =
          _stoppedLogs.length > 0
            ? this.submitBulkStoppedLogs(_stoppedLogs)
            : of([]);

        return combineLatest([submittedBulkLogs, submittedBulkStopLogs]);
      }),
      switchMap(() => {
        const clearLogs = this.removeCachedLogs();
        const clearStopLogs = this.removeStopCachedLogs();

        return combineLatest([clearLogs, clearStopLogs]);
      })
    );
  }

  private submitBulkStoppedLogs(logs: TaskLog[]) {
    return this.http.post(`task-logs/bulk-stop`, logs);
  }

  private submitBulkLogs(logs: TaskLog[]) {
    return this.http.post(`task-logs/bulk`, logs);
  }

  get currentTaskLog$(): Observable<TaskLog> {
    return this.values$.pipe(
      map(values => (values ? values.find(value => !value.end) : null))
    );
  }

  private stopRequest(taskLogId: number, body: any) {
    return this.http.patch<TaskLog>(`${this.url}/${taskLogId}/stop`, body);
  }

  private mergeLogs(logs: TaskLog[]): Observable<TaskLog[]> {
    const stoppedLogs = this.getCachedStoppedLogs();
    const cachedLogs = this.getCachedLogs();

    return combineLatest([cachedLogs, stoppedLogs]).pipe(
      map(([_cachedLogs, _stoppedLogs]) => {
        for (const log of logs) {
          const stoppedLog = _stoppedLogs.find(value => value.id === log.id);

          if (stoppedLog) {
            log.end = stoppedLog.end;
            log.extraInformation = stoppedLog.extraInformation;
            log.measurementUnits = stoppedLog.measurementUnits;
          }
        }

        return [...logs, ..._cachedLogs];
      })
    );
  }

  private removeCachedLogs(): Observable<any> {
    const promise = this.taskLogsIDB
      .idbContext()
      .then(db => db.clear(TaskIDBService.dbLogsTable));
    return from(promise);
  }

  private removeStopCachedLogs(): Observable<any> {
    const promise = this.taskLogsIDB
      .idbContext()
      .then(db => db.clear(TaskIDBService.dbStopLogsTable));
    return from(promise);
  }

  private getCachedStoppedLogs(): Observable<TaskLog[]> {
    const stoppedLogs = this.taskLogsIDB
      .idbContext()
      .then(db => db.getAll(TaskIDBService.dbStopLogsTable))
      .then(values => values || []);
    return from(stoppedLogs);
  }

  private getCachedLogs(): Observable<TaskLog[]> {
    const logs = this.taskLogsIDB
      .idbContext()
      .then(db => db.getAll(TaskIDBService.dbLogsTable))
      .then(values => values || []);
    return from(logs);
  }

  private stopLocalTaskLog(
    taskLog: TaskLog,
    body: StopLogInterface
  ): Observable<TaskLog> {
    const _taskLog = this.taskLogsIDB.stopLog(taskLog, body);
    return defer(() => _taskLog);
  }

  private createLocalTaskLog(taskLog: TaskLog): Observable<TaskLog> {
    taskLog.task = this.task;
    taskLog.employee = this.userService.user;
    const _taskLog = this.taskLogsIDB.saveLog(taskLog);
    return defer(() => _taskLog);
  }
}
